import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Database, Mail, Shield, RefreshCw } from "lucide-react";
import adminApi from "@/lib/admin-api";
import { toast } from "sonner";

export default function AdminDiagnostic() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/diagnostic/env");
      setData(data);
    } catch (err) {
      toast.error("Error cargando diagnóstico");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 text-neutral-500">Cargando diagnóstico...</div>;
  if (!data) return null;

  const missing = data.missing_critical || [];
  const allGood = missing.length === 0 && !data.resend_sender.warning;

  return (
    <div className="p-5 md:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="section-title text-3xl md:text-4xl font-black flex items-center gap-3">
            <Shield className="w-8 h-8 text-red-600" /> Diagnóstico del sistema
          </h1>
          <p className="text-neutral-500 text-sm mt-1">Verifica que todas las variables estén correctamente configuradas en producción</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-300 text-sm hover:bg-neutral-50">
          <RefreshCw className="w-4 h-4" /> Refrescar
        </button>
      </div>

      {/* Banner principal */}
      {allGood ? (
        <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-5 mb-6 flex items-start gap-3" data-testid="all-good-banner">
          <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
          <div>
            <p className="font-black text-green-900 text-lg">Todo listo ✓</p>
            <p className="text-sm text-green-800 mt-1">Todas las variables de entorno están configuradas correctamente y el dominio de Resend es válido.</p>
          </div>
        </div>
      ) : (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-5 mb-6">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-black text-red-900 text-lg">Hay variables faltantes o mal configuradas</p>
              <p className="text-sm text-red-800 mt-1">Abajo te muestro exactamente qué falta. Tienes que agregarlas en el panel de deploy de Emergent.</p>
            </div>
          </div>
          {data.resend_sender.warning && (
            <div className="bg-white border border-red-300 rounded-lg p-3 mt-3 text-sm text-red-900 font-mono text-xs">
              {data.resend_sender.warning}
            </div>
          )}
        </div>
      )}

      {/* Env vars */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-5 mb-5">
        <h3 className="font-black text-base mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4" /> Variables de entorno
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase text-neutral-500 border-b border-neutral-200">
              <th className="text-left py-2">Variable</th>
              <th className="text-left py-2">Estado</th>
              <th className="text-left py-2">Valor (enmascarado)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.env_vars).map(([k, v]) => (
              <tr key={k} className="border-b border-neutral-100">
                <td className="py-2 font-mono text-xs">{k}</td>
                <td className="py-2">
                  {v.present ? (
                    <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5" /> OK
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-red-700 text-xs font-semibold">
                      <XCircle className="w-3.5 h-3.5" /> FALTA
                    </span>
                  )}
                </td>
                <td className="py-2 text-xs text-neutral-600 font-mono">{v.value || <span className="text-red-500">(vacío)</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Resend sender */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-5 mb-5">
        <h3 className="font-black text-base mb-3 flex items-center gap-2">
          <Mail className="w-4 h-4" /> Remitente de correos (Resend)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs uppercase text-neutral-500 font-semibold">From email</p>
            <p className="font-mono text-neutral-900">{data.resend_sender.from_email_used}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-neutral-500 font-semibold">From name</p>
            <p className="font-mono text-neutral-900">{data.resend_sender.from_name_used}</p>
          </div>
        </div>
      </div>

      {/* Seed data */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-5 mb-5">
        <h3 className="font-black text-base mb-3 flex items-center gap-2">
          <Database className="w-4 h-4" /> Datos embebidos (seed_data)
        </h3>
        {data.seed_data.exists ? (
          <div>
            <p className="text-sm text-green-700 font-semibold mb-2">
              <CheckCircle2 className="w-4 h-4 inline" /> Archivos de respaldo disponibles ({data.seed_data.files.length})
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-mono">
              {data.seed_data.files.map((f) => (
                <div key={f.name} className="px-3 py-1.5 bg-neutral-50 rounded">
                  {f.name} — {(f.size / 1024).toFixed(1)}KB
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-red-700">No hay datos embebidos en /app/backend/seed_data/</p>
        )}
      </div>

      {/* DB counts */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-5 mb-5">
        <h3 className="font-black text-base mb-3 flex items-center gap-2">
          <Database className="w-4 h-4" /> Contenido actual de la base de datos
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(data.db_counts).map(([k, v]) => (
            <div key={k} className="bg-neutral-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-black text-neutral-900">{v}</p>
              <p className="text-xs text-neutral-500 uppercase tracking-wider mt-1">{k}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Fix instructions */}
      {missing.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5">
          <h3 className="font-black text-base text-orange-900 mb-3">Cómo arreglar</h3>
          <ol className="list-decimal list-inside text-sm text-orange-900 space-y-2">
            <li>Entra al panel de deploy de Emergent</li>
            <li>Busca la sección "Environment Variables" o "Variables de entorno"</li>
            <li>Agrega estas variables que faltan:
              <div className="mt-2 bg-white border border-orange-300 rounded p-3 font-mono text-xs whitespace-pre-wrap">
                {missing.map((k) => `${k}=<valor>\n`).join("")}
              </div>
            </li>
            <li>Guarda los cambios — Emergent hará restart automático</li>
            <li>Vuelve a esta página y click "Refrescar" para verificar</li>
          </ol>
        </div>
      )}
    </div>
  );
}
